import { Injectable } from '@angular/core';
import {
  ECampaignPaginationType,
  ECampaignType,
  ICampaign,
  ICampaignListingPagination,
  ICampaignPagination,
  IDates,
  IDatesPagination,
} from '../models/campaigns.model';
import { forkJoin, Observable } from 'rxjs';
import { IBaseTableResult } from '../models/table.model';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IContent } from '../models/content.model';

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  constructor(private http: HttpClient) {
  }

  getAllCampaigns(
    pagination: ICampaignPagination,
    activePagination?: ICampaignListingPagination,
  ): Observable<{
    active_campaigns: ICampaign[];
    pending_campaigns: ICampaign[];
    deleted_campaigns: ICampaign[];
    pagination: ICampaignListingPagination;
  }> {
    return forkJoin(
      this.getActiveCampaigns(
        activePagination ? activePagination.active_campaigns : pagination,
      ),
      this.getPendingCampaigns(
        activePagination ? activePagination.pending_campaigns : pagination,
      ),
      this.getDeletedCampaigns(
        activePagination ? activePagination.deleted_campaigns : pagination,
      ),
    ).pipe(
      map(res => {
        return {
          active_campaigns: res[0].items,
          pending_campaigns: res[1].items,
          deleted_campaigns: res[2].items,
          pagination: {
            active_campaigns: {
              current_page: res[0].current_page,
              has_more_data: res[0].has_more_data,
              page_size: res[0].current_page_size,
              total_pages_count: res[0].total_page_count,
              total_count: res[0].total_count,
              type: ECampaignPaginationType.ACTIVE,
              count: res[0].count,
            },
            pending_campaigns: {
              current_page: res[1].current_page,
              has_more_data: res[1].has_more_data,
              page_size: res[1].current_page_size,
              total_pages_count: res[1].total_page_count,
              total_count: res[1].total_count,
              type: ECampaignPaginationType.PENDING,
              count: res[1].count,
            },
            deleted_campaigns: {
              current_page: res[2].current_page,
              has_more_data: res[2].has_more_data,
              page_size: res[2].current_page_size,
              total_pages_count: res[2].total_page_count,
              total_count: res[2].total_count,
              type: ECampaignPaginationType.DELETED,
              count: res[2].count,
            },
          },
        };
      }),
    );
  }

  getActiveCampaigns(
    pagination?: ICampaignPagination,
  ): Observable<IBaseTableResult<ICampaign>> {
    const params: {
      active: string;
      paginated?: string;
      page_size?: string;
      page?: string;
    } = {
      active: 'true',
    };

    if (!pagination) {
      params.paginated = 'false';
    } else {
      params.page_size = pagination.page_size.toString();
      params.page = pagination.current_page.toString();
    }

    return this.http.get<IBaseTableResult<ICampaign>>(
      environment.apiUrl + '/v1/campaigns',
      {
        params,
      },
    );
  }

  getPendingCampaigns(
    pagination: ICampaignPagination,
  ): Observable<IBaseTableResult<ICampaign>> {
    return this.http.get<IBaseTableResult<ICampaign>>(
      environment.apiUrl + '/v1/campaigns',
      {
        params: {
          active: 'false',
          page_size: pagination.page_size.toString(),
          page: pagination.current_page.toString(),
        },
      },
    );
  }

  getDeletedCampaigns(
    pagination: ICampaignPagination,
  ): Observable<IBaseTableResult<ICampaign>> {
    return this.http.get<IBaseTableResult<ICampaign>>(
      environment.apiUrl + '/v1/campaigns',
      {
        params: {
          only_deleted: 'true',
          page_size: pagination.page_size.toString(),
          page: pagination.current_page.toString(),
        },
      },
    );
  }

  updateCampaign(campaign: ICampaign): Observable<{ campaign_id: number }> {
    return this.http.put<{ campaign_id: number }>(
      environment.apiUrl + `/v1/campaigns/${campaign.campaign_id}`,
      {
        ...campaign,
      },
    );
  }

  deleteCampaign(campaign: ICampaign): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      environment.apiUrl + `/v1/campaigns/${campaign.campaign_id}`,
    );
  }

  restoreCampaign(campaign: ICampaign): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      environment.apiUrl + `/v1/campaigns/${campaign.campaign_id}/restore`,
      {},
    );
  }

  getCampaignInfo(campaignId: number): Observable<ICampaign> {
    return this.http.get<ICampaign>(
      environment.apiUrl + `/v1/campaigns/${campaignId}`,
    );
  }

  createCampaign(
    campaignName: string,
    campaignType: ECampaignType,
    campaignFrequency: number,
  ): Observable<{ campaign_id: number }> {
    return this.http.post<{ campaign_id: number }>(
      environment.apiUrl + '/v1/campaigns',
      {
        campaign_name: campaignName,
        campaign_type: campaignType,
        campaign_frequency: campaignFrequency,
      },
    );
  }

  getCampaignContents(campaignId: number): Observable<IContent[]> {
    return this.http.get<IContent[]>(
      environment.apiUrl + `/v1/campaigns/${campaignId}/contents`,
      {},
    );
  }

  getDates(pagination: IDatesPagination): Observable<{
    items: IDates[];
  }> {
    return forkJoin(this.getBlockedDates(pagination)).pipe(
      map(res => {
        return {
          items: res[0].items,
          extra_data: res[0].extra_data,
        };
      }),
    );
  }

  getBlockedDates(
    pagination: IDatesPagination,
  ): Observable<IBaseTableResult<IDates>> {
    return this.http.get<IBaseTableResult<IDates>>(
      environment.apiUrl + `/v1/campaigns/blocked_dates`,
    );
  }

  createDate(
    dates: IDates[],
    accountId: number,
    isAccountManager: boolean = false,
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + '/v1/campaigns/manage_dates',
      {
        dates,
        account_id: accountId,
        to_rep: isAccountManager,
      },
    );
  }

  deleteDate(dates: IDates[]): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      environment.apiUrl + '/v1/campaigns/manage_dates',
      {
        dates,
      },
    );
  }
}
